// meetingInviteBanner — CALL-INVITATION (tickets/fable.md, Nika
// 2026-08-13: "add notification/beautiful banner when its call invitation
// on given scdheduled time or calendar meeting"). Data half for
// CallInvitationBanner.js (workspace/packages/workspace/components).
//
// The TRIGGER is server-side (server/src/domains/notifications/services/
// meetingStartingScheduler.js's interval sweep) — it writes a durable,
// deduped `meeting_starting` notification (metadata: { kind,
// calendarEventId, workspaceId, eventTitle, startAt, endAt }) per
// invitee/organizer once a CalendarEvent enters its due window. That row
// reaches this client the SAME way every other notification already does
// — subscribeNotifications.js's realtime socket push into `root.notifs`
// (no new transport, no client-side polling needed for the "appear"
// moment) — so `activeMeetingInvite` below is a pure VIEW over
// `root.notifs` + `root.rooms` + `root.activeRoomId`, not a fetcher.
//
// DISMISS = markNotificationRead (shared/functions/markNotificationRead.js,
// already el.call-registered). Reusing the notification's own durable
// `read` flag — rather than inventing a parallel pref/cookie the way
// CreditBanner's dismissal does — means "stays dismissed" is server-durable
// and cross-device for free, and there is exactly one dismissal mechanism
// to keep in sync instead of two.
//
// SCOPE DECISIONS (ticket: "decide... state the choice"):
//   - Workspace scope: an invite only surfaces while its OWN workspace is
//     the CURRENTLY ACTIVE one. Join ultimately resolves/creates a meet
//     room through the ambient active-workspace context (createRoom /
//     connectCall) — surfacing a different workspace's invite here would
//     either join the wrong tenant's room or silently fail (the exact
//     cross-org room-mismatch class roomUrl.js's header comment already
//     documents). Switching into that workspace surfaces its own due
//     invites in turn.
//   - Overlapping meetings: soonest-wins, ONE banner at a time. The next
//     due invite (if any) appears on the following render once the
//     current one is dismissed or joined — no stacking UI, consistent
//     with the single-attention-surface convention every other corner
//     overlay here already follows (CreditBanner/CreditBlocker/PersonaPill
//     never co-render more than one instance of themselves either).
//
// FA204 / frank functions-file convention (see creditGuard.js's own header
// note): every export below is SELF-CONTAINED — no export reads a sibling
// export or a module-scope helper. frank lifts each export individually;
// a bare module-scope helper reference reads as undefined after
// frank.toJSON() serialization.

/**
 * activeMeetingInvite(root) -> the single soonest qualifying
 * `meeting_starting` notification to show as a banner, or null.
 *
 * Excludes: already-read (dismissed) rows, invites for a workspace other
 * than the currently active one, events that have already ended
 * (metadata.endAt in the past), and events already joined (a meet room
 * exists for the event AND either it's the room currently connected or
 * the room itself has ended).
 */
export const activeMeetingInvite = function (root) {
  const r = root || (this && this.state && this.state.root)
  if (!r || !r.signedIn) return null
  const ws = r.workspace
  const wsId = (ws && (ws.id || ws._id)) || null
  const now = Date.now()
  const rooms = Array.isArray(r.rooms) ? r.rooms : []

  const candidates = (r.notifs || []).filter((n) => {
    if (!n || n.read) return false
    const meta = n.metadata
    if (!meta || meta.kind !== 'meeting_starting') return false
    if (wsId != null && meta.workspaceId != null && String(meta.workspaceId) !== String(wsId)) {
      return false
    }
    if (meta.endAt) {
      const endMs = Date.parse(meta.endAt)
      if (Number.isFinite(endMs) && now >= endMs) return false
    }
    const room = rooms.find((rm) => rm && rm.calendarEventId === meta.calendarEventId)
    if (room && (room.id === r.activeRoomId || room.endedAt)) return false
    return true
  })
  if (!candidates.length) return null

  candidates.sort((a, b) => {
    const am = Date.parse((a.metadata && a.metadata.startAt) || '')
    const bm = Date.parse((b.metadata && b.metadata.startAt) || '')
    return (Number.isFinite(am) ? am : Infinity) - (Number.isFinite(bm) ? bm : Infinity)
  })
  return candidates[0]
}

/**
 * joinMeetingInvite(notif) — the banner's Join action. Reuses the
 * calendar package's OWN join dispatch verbatim (eventMeetJoin.js
 * joinEventCall — find-or-create a meet_rooms row keyed by
 * calendarEventId, then hand off activeRoomId/meetView + connectCall)
 * rather than inventing a second resolution mechanism, exactly as the
 * ticket asks. Only needs { id, title } — not the full calendar event
 * object — so it works even when the event itself was never fetched into
 * this client's `root.events` (the banner is app-wide; the calendar
 * month-window fetch is not).
 *
 * Joining resolves the invite: mark it read so it never re-shows once the
 * viewer is already in the call (belt-and-suspenders with
 * activeMeetingInvite's own "already joined" exclusion above).
 */
export const joinMeetingInvite = async function (notif) {
  if (!notif || !this || !this.call) return
  const meta = notif.metadata || {}
  if (meta.calendarEventId == null) return
  await this.call('joinEventCall', {
    id: meta.calendarEventId,
    title: meta.eventTitle || notif.title
  })
  try {
    await this.call('markNotificationRead', notif.id)
  } catch (err) {
    console.warn('[joinMeetingInvite] markNotificationRead failed:', err?.message || err)
  }
}
